// TODO - Type 'any' needs to be fixed.
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { Button, CircularProgress, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import CheckboxPopover from "../../../../../design/checkbox-popover";
import { JobRolesList } from "../../../../../design/job-roles";
import useSchedulerData from "../../../controllers/use-scheduler-data";
import { JobsState, LocationOptions } from "./types";
import { isSelectAllTrue } from "./utils";

interface BMessageFormProps {
	jobs: JobsState;
	message: string;
	setMessage: (message: string) => void;
	handleClick: ({ id }: { id: number }) => void;
	handleSelectAll: (checked: boolean) => void;
	options: LocationOptions[];
	setOptions: (options: LocationOptions[]) => void;
	filteredJobs: any[] | undefined;
	workcenters: any[];
	setWorkcenters: (workcenters: any) => void;
	setFilteredJobs: (jobs: any) => void;
	workerTypeOptions: LocationOptions[];
	setWorkerTypeOptions: (options: LocationOptions[]) => void;
	shiftPatternOptions: LocationOptions[];
	setShiftPatternOptions: (options: LocationOptions[]) => void;
	isShiftPatternEnabled: boolean;
	setChangedBy: (changedBy: string) => void;
}

const BMessageForm: React.FC<BMessageFormProps> = ({
	workcenters,
	jobs,
	message,
	setMessage,
	handleClick,
	handleSelectAll,
	options,
	setOptions,
	filteredJobs,
	setWorkcenters,
	setFilteredJobs,
	workerTypeOptions,
	setWorkerTypeOptions,
	shiftPatternOptions,
	setShiftPatternOptions,
	isShiftPatternEnabled,
	setChangedBy,
}) => {
	const { t } = useTranslation();
	const {
		fetchAllLocations,
		locationsWithClusterAsync,
		fetchInitialDataStatus,
		broadcastWorkerCount,
		broadcastWorkerCountStatus,
		broadcastMessageLimit,
	} = useSchedulerData();
	const textErrorMessage = t("scheduler_message-length-error", {
		limit: broadcastMessageLimit,
	});

	const [locationAnchorElement, setLocationAnchorElement] =
		useState<null | HTMLElement>(null);
	const [workcenterAnchorElement, setWorkcenterAnchorElement] =
		useState<null | HTMLElement>(null);
	const [jobAnchorElement, setJobAnchorElement] = useState<null | HTMLElement>(
		null
	);
	const [workerTypeAnchorElement, setWorkerTypeAnchorElement] =
		useState<null | HTMLElement>(null);
	const [shiftPatternAnchorElement, setShiftPatternAnchorElement] =
		useState<null | HTMLElement>(null);

	const isOptionsAnyChecked = options.some(
		(option: { checked: boolean }) => option.checked
	);

	const isWorkcentersAnyChecked = workcenters.some(
		(option: { checked: boolean }) => option.checked
	);

	const handleLocationPopover = (event: React.MouseEvent<HTMLDivElement>) => {
		setChangedBy("location");
		setLocationAnchorElement(event.currentTarget);
	};

	const handleWorkcenterPopover = (event: React.MouseEvent<HTMLDivElement>) => {
		if (isOptionsAnyChecked) {
			setChangedBy("workcenter");
			setWorkcenterAnchorElement(event.currentTarget);
		}
	};

	const handleJobPopover = (event: React.MouseEvent<HTMLDivElement>) => {
		if (isWorkcentersAnyChecked) {
			setChangedBy("job");
			setJobAnchorElement(event.currentTarget);
		}
	};

	const handleWorkerTypePopover = (event: React.MouseEvent<HTMLDivElement>) => {
		setChangedBy("workertype");
		setWorkerTypeAnchorElement(event.currentTarget);
	};

	const handleShiftPatternPopover = (
		event: React.MouseEvent<HTMLDivElement>
	) => {
		if (isShiftPatternEnabled && isOptionsAnyChecked) {
			setChangedBy("shiftpattern");
			setShiftPatternAnchorElement(event.currentTarget);
		}
	};

	const isShiftPatternClickable = isShiftPatternEnabled && isOptionsAnyChecked;

	const getStatusText = (text: string) => {
		const arr =
			text === "location"
				? options
				: text === "workcenter"
				? workcenters
				: text === "job"
				? filteredJobs
				: text === "shiftpattern"
				? shiftPatternOptions
				: workerTypeOptions;
		const selectedOptions =
			arr && arr.length > 0
				? arr.filter((option: { checked: boolean }) => option.checked)
				: [];
		if (selectedOptions.length === 1) {
			return selectedOptions[0].label;
		} else if (arr && selectedOptions.length === arr.length && arr.length > 1) {
			return text === "location"
				? t("scheduler_all-locations-count", { count: selectedOptions.length })
				: text === "workcenter"
				? t("scheduler_all-work-centers-count", { count: selectedOptions.length })
				: text === "job"
				? t("scheduler_all-jobs-count", { count: selectedOptions.length })
				: text === "workertype"
				? t("scheduler_all-worker-types-count", { count: selectedOptions.length })
				: text === "shiftpattern"
				? t("scheduler_all-shift-patterns-count", { count: selectedOptions.length })
				: t("scheduler_all");
		} else if (selectedOptions.length > 1) {
			return t("scheduler_count-selected", { count: selectedOptions.length });
		}
		return t("scheduler_select");
	};

	return (
		<Box
			width='100%'
			display='flex'
			flexDirection='column'
			gap={2}
			height={480}
		>
			<Box>
				<TextField
					id='filled-multiline-static'
					label={t("scheduler_message")}
					multiline
					rows={3}
					defaultValue=''
					variant='filled'
					error={message?.length > Number(broadcastMessageLimit)}
					helperText={message?.length > Number(broadcastMessageLimit) ? textErrorMessage : ""}
					sx={{
						width: "100%",
						"& .Mui-focused": {
							color: "#303030",
						},
					}}
					onChange={(e) => {
						const val = e.target.value;
						setMessage(val);
					}}
				/>
				<Box
					display='flex'
					flexDirection='row'
					justifyContent='flex-end'
					mt={0.5}
				>
					<Typography variant='subtitle2' component='h2'>
						<span
							style={{
								color: message?.length > Number(broadcastMessageLimit) ? "#f44336" : "#303030",
								paddingRight:"4px",
							}}
						>
							{message?.length}
						</span>
						/ {Number(broadcastMessageLimit)}
					</Typography>
				</Box>
			</Box>
			{fetchAllLocations.status === "pending" ||
			locationsWithClusterAsync.status === "pending" ||
			fetchInitialDataStatus === "pending" ? (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						mr: "auto",
						ml: "auto",
						flexDirection: "column",
					}}
				>
					<CircularProgress size={25} />
				</Box>
			) : (
				<>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							width: "100%",
						}}
					>
						<Typography
							sx={{
								fontSize: "16px",
								fontWeight: 500,
								color: "#303030",
								marginRight: "12px",
							}}
						>
							{t("scheduler_select-recipients")}
						</Typography>
						{broadcastWorkerCountStatus === "pending" ? (
							<Box position='relative'>
								<CircularProgress size={16} />
							</Box>
						) : (
							<Typography
								sx={{
									fontSize: "16px",
									fontWeight: 400,
									color: "#303030",
									marginRight: "32px",
								}}
							>
								{t("scheduler_worker-count", { count: broadcastWorkerCount })}
							</Typography>
						)}
					</Box>
					<Box display='flex' alignItems='center'>
						<Typography
							sx={{
								fontSize: "16px",
								fontWeight: 400,
								color: "#303030",
								marginRight: "32px",
								width: "120px",
							}}
						>
							{t("scheduler_locations")}
						</Typography>
						<Box
							onClick={handleLocationPopover}
							sx={{
								padding: "8px 12px",
								background: "#f8f8f8",
								borderRadius: "4px",
								display: "flex",
								cursor: "pointer",
								width: "317px",
								justifyContent: "space-between",
							}}
						>
							<Typography variant='body1'>
								{getStatusText("location")}
							</Typography>
							{locationAnchorElement ? (
								<ArrowDropUpIcon />
							) : (
								<ArrowDropDownIcon />
							)}
						</Box>
						<CheckboxPopover
							options={options}
							anchorElement={locationAnchorElement}
							onClose={() => setLocationAnchorElement(null)}
							setOptions={setOptions}
							minWidth='317px'
							allCheckedText={t("scheduler_all-locations")}
						/>
					</Box>

					<Box display='flex' alignItems='center'>
						<Typography
							sx={{
								fontSize: "16px",
								fontWeight: 400,
								color: "#303030",
								marginRight: "32px",
								width: "120px",
							}}
						>
							{t("scheduler_work-centers")}
						</Typography>
						{workcenters.length === 0 && isOptionsAnyChecked ? (
							<Typography
								sx={{
									fontSize: "16px",
									fontWeight: 400,
									color: "#303030",
								}}
							>
								{t("scheduler_no-work-centers")}
							</Typography>
						) : (
							<>
								<Box
									onClick={handleWorkcenterPopover}
									sx={{
										padding: "8px 12px",
										background: isOptionsAnyChecked ? "#f8f8f8" : "#e0e0e0",
										borderRadius: "4px",
										display: "flex",
										cursor: isOptionsAnyChecked ? "pointer" : "default",
										width: "317px",
										justifyContent: "space-between",
										opacity: isOptionsAnyChecked ? 1 : 0.5,
									}}
								>
									<Typography variant='body1'>
										{getStatusText("workcenter")}
									</Typography>
									{workcenterAnchorElement ? (
										<ArrowDropUpIcon />
									) : (
										<ArrowDropDownIcon />
									)}
								</Box>
								<CheckboxPopover
									options={workcenters}
									anchorElement={workcenterAnchorElement}
									onClose={() => setWorkcenterAnchorElement(null)}
									setOptions={setWorkcenters}
									minWidth='317px'
									allCheckedText={t("scheduler_all-work-centers")}
								/>
							</>
						)}
					</Box>

					<Box display='flex' alignItems='center'>
						<Typography
							sx={{
								fontSize: "16px",
								fontWeight: 400,
								color: "#303030",
								marginRight: "32px",
								width: "120px",
							}}
						>
							{t("scheduler_job-roles")}
						</Typography>
						{filteredJobs && filteredJobs.length === 0 && isWorkcentersAnyChecked ? (
							<Typography
								sx={{
									fontSize: "16px",
									fontWeight: 400,
									color: "#303030",
								}}
							>
								{t("scheduler_no-job-roles")}
							</Typography>
						) : (
							<>
								<Box
									onClick={handleJobPopover}
									sx={{
										padding: "8px 12px",
										background: isWorkcentersAnyChecked ? "#f8f8f8" : "#e0e0e0",
										borderRadius: "4px",
										display: "flex",
										cursor: isWorkcentersAnyChecked ? "pointer" : "default",
										width: "317px",
										justifyContent: "space-between",
										opacity: isWorkcentersAnyChecked ? 1 : 0.5,
									}}
								>
									<Typography variant='body1'>
										{getStatusText("job")}
									</Typography>
									{jobAnchorElement ? (
										<ArrowDropUpIcon />
									) : (
										<ArrowDropDownIcon />
									)}
								</Box>
								<CheckboxPopover
									options={filteredJobs ? filteredJobs.length > 0 ? filteredJobs : [] : []}
									anchorElement={jobAnchorElement}
									onClose={() => setJobAnchorElement(null)}
									setOptions={setFilteredJobs}
									minWidth='317px'
									allCheckedText={t("scheduler_all-job-roles")}
								/>
							</>
						)}
					</Box>
					<Box display='flex' alignItems='center'>
						<Typography
							sx={{
								fontSize: "16px",
								fontWeight: 400,
								color: "#303030",
								marginRight: "32px",
								width: "120px",
							}}
						>
							{t("scheduler_worker-types")}
						</Typography>
						{workerTypeOptions && workerTypeOptions.length === 0 ? (
							<Typography
								sx={{
									fontSize: "16px",
									fontWeight: 400,
									color: "#303030",
								}}
							>
								{t("scheduler_no-worker-types")}
							</Typography>
						) : (
							<>
								<Box
									onClick={handleWorkerTypePopover}
									sx={{
										padding: "8px 12px",
										background: "#f8f8f8",
										borderRadius: "4px",
										display: "flex",
										cursor: "pointer",
										width: "317px",
										justifyContent: "space-between",
										opacity: 1,
									}}
								>
									<Typography variant='body1'>
										{getStatusText("workertype")}
									</Typography>
									{workerTypeAnchorElement ? (
										<ArrowDropUpIcon />
									) : (
										<ArrowDropDownIcon />
									)}
								</Box>
								<CheckboxPopover
									options={workerTypeOptions}
									anchorElement={workerTypeAnchorElement}
									onClose={() => setWorkerTypeAnchorElement(null)}
									setOptions={setWorkerTypeOptions}
									minWidth='317px'
									allCheckedText={t("scheduler_all-worker-types")}
								/>
							</>
						)}
					</Box>
					<Box display='flex' alignItems='flex-start'>
						<Typography
							sx={{
								fontSize: "16px",
								fontWeight: 400,
								color: "#303030",
								marginRight: "32px",
								width: "120px",
								flexShrink: 0,
								paddingTop: "8px",
							}}
						>
							{t("scheduler_shift-patterns")}
						</Typography>
						{isShiftPatternClickable && shiftPatternOptions.length === 0 ? (
							<Typography
								sx={{
									fontSize: "16px",
									fontWeight: 400,
									color: "#303030",
									paddingTop: "8px",
								}}
							>
								{t("scheduler_no-shift-patterns")}
							</Typography>
						) : (
							<Box display='flex' flexDirection='column'>
								<Box
									onClick={handleShiftPatternPopover}
									role='button'
									aria-disabled={!isShiftPatternClickable}
									sx={{
										padding: "8px 12px",
										background: isShiftPatternClickable ? "#f8f8f8" : "#e0e0e0",
										borderRadius: "4px",
										display: "flex",
										cursor: isShiftPatternClickable ? "pointer" : "default",
										width: "317px",
										justifyContent: "space-between",
										opacity: isShiftPatternClickable ? 1 : 0.5,
									}}
								>
									<Typography variant='body1'>
										{getStatusText("shiftpattern")}
									</Typography>
									{shiftPatternAnchorElement ? (
										<ArrowDropUpIcon />
									) : (
										<ArrowDropDownIcon />
									)}
								</Box>
								{!isShiftPatternEnabled ? (
									<Typography
										variant='caption'
										sx={{ color: "#6c6c6c", marginTop: "4px", maxWidth: "317px" }}
									>
										{t("scheduler_shift-patterns-full-time-only")}
									</Typography>
								) : null}
								<CheckboxPopover
									options={shiftPatternOptions}
									anchorElement={shiftPatternAnchorElement}
									onClose={() => setShiftPatternAnchorElement(null)}
									setOptions={setShiftPatternOptions}
									minWidth='317px'
									allCheckedText={t("scheduler_all-shift-patterns")}
								/>
							</Box>
						)}
					</Box>
				</>
			)}
		</Box>
	);
};

export default BMessageForm;
